import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import {
  FinancialPositionDetailDialog,
  type FinancialPositionItem,
} from '@/components/spending/financial-position-detail-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { fundTypeLabel } from '@/components/shared/fund-type-select';
import { Pagination } from '@/components/shared/pagination';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { CONTRIBUTION_METHODS } from '@/lib/contribution-methods';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { formatDate } from '@/utils/format';

import {
  BALANCE_SHEET_REPORT_QUERY_KEY,
  BalanceSheetReportPanel,
} from './balance-sheet-report-panel';

type PositionTab = 'statement' | 'accounts' | 'funds';

interface FundCollection {
  id: string;
  name: string;
  fundType: string;
  customTypeName: string | null;
  currency: string;
  byMethod: Record<string, number>;
  totalMinor: number;
}

interface MoneyAmount {
  currency: string;
  amountMinor: number;
}

type PositionKind =
  | 'bank_account'
  | 'cash'
  | 'mobile_money'
  | 'hubtel_account'
  | 'fixed_asset'
  | 'liability';

const POSITION_KINDS: PositionKind[] = [
  'bank_account',
  'cash',
  'mobile_money',
  'hubtel_account',
  'fixed_asset',
  'liability',
];

const emptyCreateForm = (currency: string) => ({
  kind: 'bank_account' as PositionKind,
  name: '',
  balanceMinor: 0,
  currency,
  balanceAsOfDate: new Date().toISOString().slice(0, 10),
  reason: '',
});

export const FinancialPositionPage = () => {
  const { t } = useTranslation('spending');
  const { t: tGiving } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const { t: tErrors } = useTranslation('errors');
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const baseCurrency = church?.baseCurrency ?? 'GHS';

  const canManage = hasPermission(membership, PERMISSIONS.GIVING_ADJUST_BALANCE);
  const givingEnabled = useChurchStore((state) =>
    state.enabledModules.some((module) => module.code === 'giving'),
  );
  const canViewFunds = givingEnabled && hasPermission(membership, PERMISSIONS.GIVING_VIEW);
  const [fundsPage, setFundsPage] = useState(1);
  const [fundsPageSize, setFundsPageSize] = useState(25);
  const tabParam = searchParams.get('tab');
  const tab: PositionTab =
    tabParam === 'accounts' || (tabParam === 'funds' && canViewFunds) ? tabParam : 'statement';

  const setTab = (next: PositionTab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'statement') nextParams.delete('tab');
    else nextParams.set('tab', next);
    nextParams.delete('create');
    setSearchParams(nextParams, { replace: true });
  };

  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState(() => emptyCreateForm(baseCurrency));

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'summary', fundsPage, fundsPageSize],
    enabled: canViewFunds && tab === 'funds',
    queryFn: () =>
      api.get<{
        data: FundCollection[];
        page: { page: number; pageSize: number; totalItems: number; totalPages: number };
        totals: {
          byMethod: Array<{ method: string; amounts: MoneyAmount[] }>;
          total: MoneyAmount[];
        };
      }>('/giving/funds/collections', {
        query: { page: fundsPage, pageSize: fundsPageSize },
      }),
  });

  const items = useQuery({
    queryKey: ['financial-position', 'items'],
    queryFn: () => api.get<{ data: FinancialPositionItem[] }>('/financial-position/items'),
  });

  const selected = useMemo(
    () => items.data?.data.find((row) => row.id === selectedId) ?? null,
    [items.data?.data, selectedId],
  );

  const kindOptions = useMemo(
    () =>
      POSITION_KINDS.map((kind) => ({
        value: kind,
        label: t(`financialPosition.kind.${kind}`),
      })),
    [t],
  );

  const columns: DataTableColumn<FinancialPositionItem>[] = [
    {
      id: 'kind',
      header: t('financialPosition.columns.kind'),
      cell: (row) => t(`financialPosition.kind.${row.kind}`),
    },
    {
      id: 'name',
      header: t('financialPosition.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'balance',
      header: t('financialPosition.columns.balance'),
      align: 'right',
      money: (row) => ({ amount: row.balanceMinor, currency: row.currency }),
    },
    {
      id: 'balanceAsOfDate',
      header: t('financialPosition.columns.asOf'),
      cell: (row) => formatDate(row.balanceAsOfDate),
    },
  ];

  const showInKind = (funds.data?.totals.byMethod ?? []).some(
    (item) => item.method === 'in_kind' && item.amounts.some((amount) => amount.amountMinor !== 0),
  );
  const collectionMethods = showInKind
    ? [...CONTRIBUTION_METHODS, 'in_kind' as const]
    : [...CONTRIBUTION_METHODS];
  const fundColumns: DataTableColumn<FundCollection>[] = [
    {
      id: 'name',
      header: t('financialPosition.funds.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'type',
      header: t('financialPosition.funds.columns.type'),
      cell: (row) => fundTypeLabel(row, tGiving),
    },
    ...collectionMethods.map((method) => ({
      id: method,
      header: tGiving(`methods.${method}`),
      align: 'right' as const,
      money: (row: FundCollection) => ({
        amount: row.byMethod[method] ?? 0,
        currency: row.currency,
      }),
    })),
    {
      id: 'total',
      header: t('financialPosition.funds.columns.total'),
      align: 'right',
      money: (row) => ({ amount: row.totalMinor, currency: row.currency }),
    },
  ];

  const openItem = (row: FinancialPositionItem) => {
    setSelectedId(row.id);
    setDetailOpen(true);
  };

  const openCreate = () => {
    setCreateForm(emptyCreateForm(baseCurrency));
    setCreateOpen(true);
  };

  useEffect(() => {
    if (searchParams.get('create') !== '1' || !canManage) return;
    setCreateForm(emptyCreateForm(baseCurrency));
    setCreateOpen(true);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'accounts');
    next.delete('create');
    setSearchParams(next, { replace: true });
  }, [baseCurrency, canManage, searchParams, setSearchParams]);

  const createItem = useMutation({
    mutationFn: () =>
      api.post<FinancialPositionItem>('/financial-position/items', {
        kind: createForm.kind,
        name: createForm.name,
        balanceMinor: createForm.balanceMinor,
        currency: createForm.currency,
        balanceAsOfDate: createForm.balanceAsOfDate,
        reason: createForm.reason.trim(),
      }),
    onSuccess: async (row) => {
      await queryClient.invalidateQueries({ queryKey: ['financial-position'] });
      await queryClient.invalidateQueries({ queryKey: BALANCE_SHEET_REPORT_QUERY_KEY });
      setCreateOpen(false);
      setSelectedId(row.id);
      setDetailOpen(true);
      toast.success(t('financialPosition.saved'));
    },
    onError: (error) => {
      if (error instanceof ApiError) toast.error(tErrors(error.code, error.params));
      else toast.error(tCommon('toast.somethingWentWrong'));
    },
  });

  const createReasonValid = createForm.reason.trim().length >= 3;
  const createCanSave =
    createForm.name.trim().length > 0 && createReasonValid && !createItem.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('financialPosition.title')}
        description={
          tab === 'statement'
            ? t('financialPosition.statementDescription')
            : tab === 'funds'
              ? t('financialPosition.funds.description')
              : t('financialPosition.description')
        }
        headerActions={
          tab === 'accounts' && canManage ? (
            <Button onClick={openCreate}>{t('financialPosition.add')}</Button>
          ) : undefined
        }
      />

      <Tabs value={tab} onValueChange={(value) => setTab(value as PositionTab)}>
        <TabsList>
          <TabsTrigger value="statement">{t('financialPosition.tabs.statement')}</TabsTrigger>
          <TabsTrigger value="accounts">{t('financialPosition.tabs.accounts')}</TabsTrigger>
          {canViewFunds ? (
            <TabsTrigger value="funds">{t('financialPosition.tabs.funds')}</TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="statement">
          <BalanceSheetReportPanel />
        </TabsContent>

        <TabsContent value="funds" className="space-y-4">
          <DataTable
            columns={fundColumns}
            rows={funds.data?.data ?? []}
            rowKey={(row) => row.id}
            loading={funds.isLoading}
            onRowClick={(row) => navigate(`/giving/funds/${row.id}`)}
            totals={
              funds.data
                ? {
                    ...Object.fromEntries(
                      funds.data.totals.byMethod
                        .filter((item) => collectionMethods.includes(item.method as (typeof collectionMethods)[number]))
                        .map((item) => [
                          item.method,
                          item.amounts.map((amount) => ({
                            amount: amount.amountMinor,
                            currency: amount.currency,
                          })),
                        ]),
                    ),
                    total: funds.data.totals.total.map((amount) => ({
                      amount: amount.amountMinor,
                      currency: amount.currency,
                    })),
                  }
                : undefined
            }
            empty={
              <p className="text-body-sm text-fg-secondary">{t('financialPosition.funds.empty')}</p>
            }
          />
          {funds.data?.page && funds.data.page.totalPages > 1 ? (
            <Pagination
              page={funds.data.page.page}
              pageSize={funds.data.page.pageSize}
              totalItems={funds.data.page.totalItems}
              totalPages={funds.data.page.totalPages}
              onPageChange={setFundsPage}
              onPageSizeChange={(next) => {
                setFundsPageSize(next);
                setFundsPage(1);
              }}
              labels={{
                showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
                perPage: tCommon('pagination.perPage'),
                first: tCommon('pagination.first'),
                previous: tCommon('pagination.previous'),
                next: tCommon('pagination.next'),
                last: tCommon('pagination.last'),
              }}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="accounts" className="space-y-6">
          <p className="text-body-sm text-fg-muted">{t('financialPosition.helper')}</p>

          <DataTable
            columns={columns}
            rows={items.data?.data ?? []}
            rowKey={(row) => row.id}
            loading={items.isLoading}
            empty={
              <div className="py-8 text-center">
                <p className="text-card-title text-fg">{t('financialPosition.empty.title')}</p>
                <p className="mt-1 text-body-sm text-fg-muted">{t('financialPosition.empty.body')}</p>
              </div>
            }
            onRowClick={openItem}
          />
        </TabsContent>
      </Tabs>

      <FinancialPositionDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        item={selected}
        canManage={canManage}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('financialPosition.add')}</DialogTitle>
            <DialogDescription>{t('financialPosition.formDescription')}</DialogDescription>
          </DialogHeader>
          <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
            <FormField label={t('financialPosition.kindLabel')} required>
              {() => (
                <Listbox
                  popoverPortal="dialog"
                  value={createForm.kind}
                  onChange={(value) =>
                    setCreateForm((current) => ({ ...current, kind: value as PositionKind }))
                  }
                  options={kindOptions}
                />
              )}
            </FormField>
            <FormField label={t('financialPosition.name')} required>
              {() => (
                <Input
                  value={createForm.name}
                  onChange={(event) =>
                    setCreateForm((current) => ({ ...current, name: event.target.value }))
                  }
                />
              )}
            </FormField>
            <FormField label={t('financialPosition.balance')} required>
              {() => (
                <MoneyInput
                  value={createForm.balanceMinor}
                  onChange={(value) =>
                    setCreateForm((current) => ({
                      ...current,
                      balanceMinor: value ?? 0,
                    }))
                  }
                  currency={createForm.currency}
                />
              )}
            </FormField>
            <FormField label={t('financialPosition.asOf')} required>
              {() => (
                <DatePicker
                  popoverPortal="dialog"
                  value={createForm.balanceAsOfDate}
                  onChange={(value) =>
                    setCreateForm((current) => ({
                      ...current,
                      balanceAsOfDate: value ?? current.balanceAsOfDate,
                    }))
                  }
                />
              )}
            </FormField>
            <FormField label={t('financialPosition.openingReason')} required>
              {() => (
                <Textarea
                  value={createForm.reason}
                  onChange={(event) =>
                    setCreateForm((current) => ({ ...current, reason: event.target.value }))
                  }
                  rows={3}
                  maxLength={2000}
                  placeholder={t('financialPosition.openingReasonPlaceholder')}
                />
              )}
            </FormField>
          </DialogBody>
          <DialogFooter className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {tCommon('action.cancel')}
            </Button>
            <Button onClick={() => createItem.mutate()} disabled={!createCanSave}>
              {tCommon('action.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
